import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapFromDb(row) { return snakeToCamel(row); }

function buildQuery(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value == null || value === '') return;
    params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useVoicenotes(filters = {}) {
  return useQuery({
    queryKey: [...queryKeys.voicenotes.all, filters],
    queryFn: async () => {
      const data = await api.get(`/voicenotes${buildQuery(filters)}`);
      return data.map(mapFromDb);
    },
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });
}

export function useProductions() {
  return useQuery({
    queryKey: [...queryKeys.voicenotes.all, 'productions'],
    queryFn: async () => api.get('/voicenotes/productions'),
  });
}

export function useProductionOutline(slug) {
  return useQuery({
    queryKey: queryKeys.voicenotes.productionOutline(slug),
    queryFn: async () => {
      const data = await api.get(`/voicenotes/production/${slug}/outline`);
      return {
        draft: data.draft ? mapFromDb(data.draft) : null,
        finals: (data.finals || []).map(mapFromDb),
      };
    },
    enabled: !!slug,
  });
}

export function useUpdateVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }) => {
      return api.put(`/voicenotes/${id}`, fields);
    },
    // Optimistic so board drags don't snap back before the refetch lands.
    // The voicenotes key also matches productions/outline queries, so guard on Array.
    onMutate: async ({ id, ...fields }) => {
      await qc.cancelQueries({ queryKey: queryKeys.voicenotes.all });
      const prev = qc.getQueriesData({ queryKey: queryKeys.voicenotes.all });
      const patch = snakeToCamel(fields);
      qc.setQueriesData({ queryKey: queryKeys.voicenotes.all }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((n) => (n.id === id ? { ...n, ...patch } : n));
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSuccess: (_data, variables) => {
      // Skip the pure un-queue toggle ({ queued_at: null }) — that's UI-state churn, not a record edit.
      const changed = Object.keys(variables).filter((k) => k !== 'id');
      if (changed.length === 1 && changed[0] === 'queued_at') return;
      logAction({
        action: 'Update Voice Note',
        description: `Updated voice note ${variables.id}`,
        details: { id: variables.id, status: variables.status, archivedAt: variables.archived_at, notes: variables.notes },
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
    },
  });
}

/**
 * Re-board / confirm a note's classification. Omit `board` to just confirm the
 * current one ("looks right"). Sets board_confidence='confirmed' server-side,
 * locking it against future auto-triage. Optimistic.
 */
export function useReboardVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, board, category }) => api.put(`/voicenotes/${id}/board`, { board, category }),
    onMutate: async ({ id, board, category }) => {
      await qc.cancelQueries({ queryKey: queryKeys.voicenotes.all });
      const prev = qc.getQueriesData({ queryKey: queryKeys.voicenotes.all });
      qc.setQueriesData({ queryKey: queryKeys.voicenotes.all }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((n) => (n.id === id
          ? { ...n, boardConfidence: 'confirmed', ...(board ? { board, category: category ?? null } : {}) }
          : n));
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data)); },
    onSuccess: (_data, variables) => {
      logAction({
        action: 'Reclassify Voice Note',
        description: `Reclassified voice note ${variables.id} to ${variables.board || 'current'}/${variables.category || ''}`,
        details: { id: variables.id, board: variables.board, category: variables.category },
      });
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all }); },
  });
}

export function useUpdateProductionVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }) => api.put(`/voicenotes/${id}/production`, fields),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
      logAction({
        action: 'Update Production Voice Note',
        description: `Updated production fields on voice note ${variables.id}`,
        details: { id: variables.id },
      });
    },
  });
}

export function useGenerateProductionFinalOutline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug) => api.post(`/voicenotes/production/${slug}/outline/final`),
    onSuccess: (_data, slug) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
      logAction({
        action: 'Generate Production Outline',
        description: `Generated final production outline for ${slug}`,
        details: { slug },
      });
    },
  });
}

export function useDeleteVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.del(`/voicenotes/${id}`);
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
      logAction({
        action: 'Delete Voice Note',
        description: `Deleted voice note ${id}`,
        details: { id },
      });
    },
  });
}

export function useQueueVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => api.post(`/voicenotes/${id}/queue`),
    // Optimistic so the queued chip flips immediately (guard on Array — the
    // voicenotes key prefix also matches productions/outline queries).
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.voicenotes.all });
      const prev = qc.getQueriesData({ queryKey: queryKeys.voicenotes.all });
      qc.setQueriesData({ queryKey: queryKeys.voicenotes.all }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((n) => (n.id === id ? { ...n, queuedAt: new Date().toISOString() } : n));
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prev?.forEach(([key, data]) => { qc.setQueryData(key, data); });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
    },
  });
}

export function useDoneVoicenote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => api.post(`/voicenotes/${id}/done`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicenotes.all });
      logAction({
        action: 'Complete Voice Note',
        description: `Marked voice note ${id} done`,
        details: { id },
      });
    },
  });
}
